import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './OnboardingQuestionnaire.css';

const OnboardingQuestionnaire = ({ onComplete }) => {
    const [formData, setFormData] = useState({
        companyName: '',
        industry: '',
        role: '',
        businessDescription: ''
    });
    const [errors, setErrors] = useState({});

    const industries = [
        { value: 'advertising', label: 'Advertising/Marketing Agency' },
        { value: 'ecommerce', label: 'E-commerce' },
        { value: 'entertainment', label: 'Online Entertainment/Media' },
        { value: 'education', label: 'Education/Training/B2B Services' },
        { value: 'finance', label: 'Finance & Insurance' },
        { value: 'utility', label: 'Utility App/Tool App' },
        { value: 'other', label: 'Other' }
    ];

    const roles = [
        { value: 'executive', label: 'C-Level Executive/Decision Maker' },
        { value: 'marketing', label: 'Marketing Department' },
        { value: 'media_buyer', label: 'Media Buyer' },
        { value: 'sales', label: 'Sales Department' },
        { value: 'operations', label: 'Operation Department' },
        { value: 'product_manager', label: 'Product Manager' },
        { value: 'it', label: 'IT/Technology Department' },
        { value: 'other', label: 'Other' },
        { value: 'agency', label: 'Agency' },
        { value: 'freelancer', label: 'Freelancer/Solo Entrepreneur' }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }
        
        if (!formData.industry) {
            newErrors.industry = 'Please select an industry';
        }
        
        if (!formData.role) {
            newErrors.role = 'Please select your role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            // Pass formData directly to parent component (no localStorage storage)
            // Data will be sent to backend after first scan
            onComplete(formData);
        }
    };

    const handleSkip = () => {
        // Skip onboarding - no data will be saved
        onComplete(null);
    };

    return (
        <div className="onboarding-container">
            <div className="onboarding-card">
                <div className="onboarding-header">
                    <h2>Welcome to Web Scanner!</h2>
                    <p>Help us personalize your experience by answering a few quick questions.</p>
                </div>

                <form onSubmit={handleSubmit} className="onboarding-form">
                    {/* Question 1: Company Name */}
                    <div className="form-group">
                        <label htmlFor="companyName">
                            1. What's your company name? <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Enter your company name"
                            className={errors.companyName ? 'error' : ''}
                        />
                        {errors.companyName && (
                            <span className="error-message">{errors.companyName}</span>
                        )}
                    </div>

                    {/* Question 2: Industry */}
                    <div className="form-group">
                        <label htmlFor="industry">
                            2. Which industry best describes your business? <span className="required">*</span>
                        </label>
                        <select
                            id="industry"
                            name="industry"
                            value={formData.industry}
                            onChange={handleChange}
                            className={errors.industry ? 'error' : ''}
                        >
                            <option value="">Select an industry</option>
                            {industries.map(ind => (
                                <option key={ind.value} value={ind.value}>
                                    {ind.label}
                                </option>
                            ))}
                        </select>
                        {errors.industry && (
                            <span className="error-message">{errors.industry}</span>
                        )}
                    </div>

                    {/* Question 3: Role */}
                    <div className="form-group">
                        <label htmlFor="role">
                            3. What is your role in the business or company? <span className="required">*</span>
                        </label>
                        <select
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className={errors.role ? 'error' : ''}
                        >
                            <option value="">Select your role</option>
                            {roles.map(role => (
                                <option key={role.value} value={role.value}>
                                    {role.label}
                                </option>
                            ))}
                        </select>
                        {errors.role && (
                            <span className="error-message">{errors.role}</span>
                        )}
                    </div>

                    {/* Question 4: Business Description (Optional) */}
                    <div className="form-group">
                        <label htmlFor="businessDescription">
                            4. Could you briefly describe your business? <span className="optional">(optional)</span>
                        </label>
                        <textarea
                            id="businessDescription"
                            name="businessDescription"
                            value={formData.businessDescription}
                            onChange={handleChange}
                            placeholder="Tell us about your business..."
                            rows="4"
                        />
                    </div>

                    {/* Submit Buttons */}
                    <div className="form-actions">
                        <button type="submit" className="btn-primary">
                            Get Started
                        </button>
                        <button type="button" onClick={handleSkip} className="btn-secondary">
                            Skip for Now
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

OnboardingQuestionnaire.propTypes = {
    onComplete: PropTypes.func.isRequired
};

export default OnboardingQuestionnaire;
